import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Switch,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Event,
  ArrowBack,
  Add,
  Edit,
  Delete,
  Notifications,
  Schedule
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface EventTrigger {
  id: string;
  name: string;
  event_type: string;
  trigger_condition: string;
  action: string;
  is_active: boolean;
  created_at: string;
}

export const EventTriggersPage: React.FC = () => {
  const navigate = useNavigate();
  const [triggers, setTriggers] = useState<EventTrigger[]>([
    {
      id: '1',
      name: 'New Product Launch',
      event_type: 'product_launch',
      trigger_condition: 'Product status = "published"',
      action: 'Create announcement post',
      is_active: true,
      created_at: '2024-01-15'
    },
    {
      id: '2',
      name: 'Weekly Analytics',
      event_type: 'scheduled',
      trigger_condition: 'Every Monday 9:00 AM',
      action: 'Post performance summary',
      is_active: false,
      created_at: '2024-01-10'
    }
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newTrigger, setNewTrigger] = useState({
    name: '',
    event_type: 'product_launch',
    trigger_condition: '',
    action: ''
  });

  const eventTypes = [
    { value: 'product_launch', label: 'Product Launch' },
    { value: 'scheduled', label: 'Scheduled Event' },
    { value: 'engagement_threshold', label: 'Engagement Threshold' },
    { value: 'follower_milestone', label: 'Follower Milestone' },
    { value: 'trending_topic', label: 'Trending Topic' }
  ];

  const toggleTrigger = async (id: string) => {
    setTriggers(prev => prev.map(trigger => 
      trigger.id === id 
        ? { ...trigger, is_active: !trigger.is_active }
        : trigger
    ));
  };

  const deleteTrigger = async (id: string) => {
    setTriggers(prev => prev.filter(trigger => trigger.id !== id));
  };

  const createTrigger = async () => {
    if (!newTrigger.name || !newTrigger.trigger_condition || !newTrigger.action) return;

    const trigger: EventTrigger = {
      id: Date.now().toString(),
      ...newTrigger,
      is_active: true,
      created_at: new Date().toISOString().split('T')[0]
    };

    setTriggers(prev => [...prev, trigger]);
    setNewTrigger({ name: '', event_type: 'product_launch', trigger_condition: '', action: '' });
    setDialogOpen(false);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2 }}
          >
            Back to Dashboard
          </Button>
          <Event color="primary" sx={{ mr: 2, fontSize: 32 }} />
          <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
            Event Triggers
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setDialogOpen(true)}
          >
            Create Trigger
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Automate your posting based on events and conditions
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Active Triggers
              </Typography>
              {triggers.length === 0 ? (
                <Typography color="text.secondary">
                  No triggers configured yet
                </Typography>
              ) : (
                <List>
                  {triggers.map((trigger) => (
                    <ListItem key={trigger.id} divider>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {trigger.name}
                            <Chip 
                              label={trigger.event_type.replace('_', ' ')}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                            <Chip
                              label={trigger.is_active ? 'Active' : 'Inactive'}
                              size="small"
                              color={trigger.is_active ? 'success' : 'default'}
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Condition: {trigger.trigger_condition}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Action: {trigger.action}
                            </Typography>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          edge="end"
                          checked={trigger.is_active}
                          onChange={() => toggleTrigger(trigger.id)}
                        />
                        <IconButton edge="end" sx={{ ml: 1 }}>
                          <Edit />
                        </IconButton>
                        <IconButton 
                          edge="end" 
                          color="error"
                          onClick={() => deleteTrigger(trigger.id)}
                        >
                          <Delete />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Trigger Statistics
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Total Triggers"
                    secondary={triggers.length}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Active Triggers"
                    secondary={triggers.filter(t => t.is_active).length}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Posts This Week"
                    secondary="12"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Triggered Events"
                    secondary="8"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Event Types
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Product Launch"
                    secondary="Trigger when new products are published"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Scheduled"
                    secondary="Time-based automated posting"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Engagement"
                    secondary="Based on likes, comments, shares"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Follower Milestone"
                    secondary="Celebrate follower achievements"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Trigger</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Trigger Name"
            value={newTrigger.name}
            onChange={(e) => setNewTrigger(prev => ({ ...prev, name: e.target.value }))}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Event Type</InputLabel>
            <Select
              value={newTrigger.event_type}
              onChange={(e) => setNewTrigger(prev => ({ ...prev, event_type: e.target.value }))}
              label="Event Type"
            >
              {eventTypes.map(type => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Trigger Condition"
            value={newTrigger.trigger_condition}
            onChange={(e) => setNewTrigger(prev => ({ ...prev, trigger_condition: e.target.value }))}
            margin="normal"
            placeholder="e.g., Every Monday 9:00 AM"
          />
          <TextField
            fullWidth
            label="Action to Perform"
            value={newTrigger.action}
            onChange={(e) => setNewTrigger(prev => ({ ...prev, action: e.target.value }))}
            margin="normal"
            placeholder="e.g., Create announcement post"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={createTrigger} variant="contained">
            Create Trigger
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EventTriggersPage; 